import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, authedFileUrl } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, FileText } from "lucide-react";

export default function PayStatements() {
  const [statements, setStatements] = useState([]);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");

  useEffect(() => {
    api.get("/pay-statements").then((r) => setStatements(r.data)).catch(() => {});
  }, []);

  const bulkExport = () => {
    if (!exportStart || !exportEnd) return;
    window.open(authedFileUrl(`/quickbooks/export.csv?period_start=${exportStart}&period_end=${exportEnd}`), "_blank");
  };

  return (
    <div className="space-y-6" data-testid="statements-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Pay Statements</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Saved pay runs. Click a statement # to open, mark paid, print, email, or export to QuickBooks.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)} className="h-9 w-40" data-testid="qb-start" />
          <Input type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)} className="h-9 w-40" data-testid="qb-end" />
          <Button variant="outline" size="sm" onClick={bulkExport} data-testid="qb-bulk-export">
            <Download size={14} className="mr-1" /> QuickBooks CSV
          </Button>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-statement-btn">
            <Link to="/pay-calculator"><FileText size={14} className="mr-1" /> New in Pay Calculator</Link>
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Statement #</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Driver</th>
                <th className="px-4 py-2.5">Pay Date</th>
                <th className="px-4 py-2.5">Period</th>
                <th className="px-4 py-2.5 text-right">Gross</th>
                <th className="px-4 py-2.5 text-right">Net</th>
                <th className="px-4 py-2.5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {statements.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No statements yet.</td></tr>
              )}
              {statements.map((s) => (
                <tr key={s.statement_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs">
                    <Link to={`/pay-statements/${s.statement_id}`} className="text-blue-700 hover:underline" data-testid={`statement-${s.statement_number}`}>
                      {s.statement_number}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={(s.status || "draft") === "paid" ? "pill-green" : "pill-amber"}>
                      {(s.status || "draft").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{s.driver_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{s.driver_display_id}</div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{s.pay_date || s.created_at.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{s.period_start} → {s.period_end}</td>
                  <td className="px-4 py-2.5 text-right font-mono">${s.totals.gross.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-blue-700">${s.totals.net.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/pay-statements/${s.statement_id}`}>Open</Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
